import {
  CalendarIcon,
  CheckCircleIcon,
  CurrencyPoundIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { billingService } from "../../services/billingService";
import { SubscriptionChangePreview as PreviewData } from "../../types/billing";

interface SubscriptionChangePreviewProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  organizationId: string;
  planType?: string;
  billingCycle?: string;
  editorCount?: number;
  loading?: boolean;
  currencyCode?: string;
}

export const SubscriptionChangePreview: React.FC<
  SubscriptionChangePreviewProps
> = ({
  open,
  onClose,
  onConfirm,
  organizationId,
  planType,
  billingCycle,
  editorCount,
  loading: parentLoading,
  currencyCode,
}) => {
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && planType && billingCycle) {
      fetchPreview();
    }
  }, [open, planType, billingCycle, editorCount]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await billingService.previewSubscriptionChange(
        organizationId,
        planType as any,
        billingCycle as any,
        editorCount || 1,
      );
      setPreview(data);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === "string") {
        setError(errorDetail);
      } else if (errorDetail && typeof errorDetail === "object") {
        setError(
          errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail),
        );
      } else {
        setError("Failed to load preview");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    const code = (currencyCode || "GBP").toUpperCase();
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: code,
    }).format(amount);
  };

  const getChangeType = () => {
    if (!preview) return "";

    if (preview.changes.plan) {
      return preview.is_upgrade ? "Plan Upgrade" : "Plan Downgrade";
    } else if (preview.changes.billing_cycle) {
      return "Billing Cycle Change";
    } else if (preview.changes.editors) {
      return "Editor Count Change";
    }
    return "Subscription Change";
  };

  const getChangeIcon = () => {
    if (!preview) return null;
    return preview.is_upgrade ? (
      <CheckCircleIcon className="h-6 w-6 text-green-600" />
    ) : (
      <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
    );
  };

  const getChangeSummary = () => {
    if (!preview) return [];

    const changes = [];
    if (preview.changes.plan) {
      changes.push({
        label: "Plan",
        from: billingService.getPlanDisplayName(preview.changes.plan.from),
        to: billingService.getPlanDisplayName(preview.changes.plan.to),
      });
    }
    if (preview.changes.billing_cycle) {
      changes.push({
        label: "Billing Cycle",
        from: preview.changes.billing_cycle.from,
        to: preview.changes.billing_cycle.to,
      });
    }
    if (preview.changes.editors) {
      changes.push({
        label: "Editor Seats",
        from: preview.changes.editors.from.toString(),
        to: preview.changes.editors.to.toString(),
      });
    }
    return changes;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500/75"
          onClick={onClose}
        />
        <div className="relative bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading preview...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto" />
              <p className="mt-2 text-sm text-red-600">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          ) : preview ? (
            <>
              {/* Header */}
              <div className="flex items-center mb-6">
                {getChangeIcon()}
                <h2 className="ml-3 text-xl font-semibold text-gray-900">
                  {getChangeType()}
                </h2>
              </div>

              {/* Changes Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  What&apos;s Changing
                </h3>
                <div className="space-y-2">
                  {getChangeSummary().map((change, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <span className="text-gray-500 w-24">
                        {change.label}:
                      </span>
                      <span className="font-medium">{change.from}</span>
                      <span className="mx-2 text-gray-400">→</span>
                      <span className="font-medium text-blue-600">
                        {change.to}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cost Comparison */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Current Cost
                  </h4>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(preview.current_subscription.monthly_cost)}
                    <span className="text-sm font-normal text-gray-500">
                      /month
                    </span>
                  </p>
                  {preview.current_subscription.billing_cycle === "annual" && (
                    <p className="text-sm text-gray-500 mt-1">
                      {formatCurrency(preview.current_subscription.annual_cost)}
                      /year
                    </p>
                  )}
                  {/* Always show pricing breakdown for clarity */}
                  {(preview.current_subscription.base_cost > 0 ||
                    preview.current_subscription.editor_cost > 0) && (
                    <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-600 space-y-1">
                      <div className="font-medium text-gray-700">
                        Current breakdown:
                      </div>
                      <div className="flex justify-between">
                        <span>
                          {billingService.getPlanDisplayName(
                            preview.current_subscription.plan_type,
                          )}{" "}
                          base plan (includes 1 editor)
                        </span>
                        <span className="font-medium">
                          {formatCurrency(
                            preview.current_subscription.base_cost,
                          )}
                        </span>
                      </div>
                      {preview.current_subscription.editor_cost > 0 && (
                        <div className="flex justify-between">
                          <span>
                            {preview.current_subscription.editor_count - 1}{" "}
                            additional editor
                            {preview.current_subscription.editor_count > 2
                              ? "s"
                              : ""}
                          </span>
                          <span className="font-medium">
                            +
                            {formatCurrency(
                              preview.current_subscription.editor_cost,
                            )}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between pt-1 border-t border-gray-200 font-medium">
                        <span>
                          Total{" "}
                          {preview.current_subscription.billing_cycle ===
                          "monthly"
                            ? "monthly"
                            : "annual"}
                        </span>
                        <span>
                          {formatCurrency(
                            preview.current_subscription.total_cost,
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    New Cost
                  </h4>
                  <p className="text-2xl font-semibold text-blue-900">
                    {formatCurrency(preview.new_subscription.monthly_cost)}
                    <span className="text-sm font-normal text-gray-600">
                      /month
                    </span>
                  </p>
                  {preview.new_subscription.billing_cycle === "annual" && (
                    <p className="text-sm text-gray-600 mt-1">
                      {formatCurrency(preview.new_subscription.annual_cost)}
                      /year
                    </p>
                  )}
                  {/* Always show pricing breakdown for clarity */}
                  {(preview.new_subscription.base_cost > 0 ||
                    preview.new_subscription.editor_cost > 0) && (
                    <div className="mt-3 pt-2 border-t border-blue-200 text-xs text-gray-600 space-y-1">
                      <div className="font-medium text-gray-700">
                        Price breakdown:
                      </div>
                      <div className="flex justify-between">
                        <span>
                          {billingService.getPlanDisplayName(
                            preview.new_subscription.plan_type,
                          )}{" "}
                          base plan (includes 1 editor)
                        </span>
                        <span className="font-medium">
                          {formatCurrency(preview.new_subscription.base_cost)}
                        </span>
                      </div>
                      {preview.new_subscription.editor_cost > 0 && (
                        <div className="flex justify-between">
                          <span>
                            {preview.new_subscription.editor_count - 1}{" "}
                            additional editor
                            {preview.new_subscription.editor_count > 2
                              ? "s"
                              : ""}
                          </span>
                          <span className="font-medium">
                            +
                            {formatCurrency(
                              preview.new_subscription.editor_cost,
                            )}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between pt-1 border-t border-blue-200 font-medium">
                        <span>
                          Total{" "}
                          {preview.new_subscription.billing_cycle === "monthly"
                            ? "monthly"
                            : "annual"}
                        </span>
                        <span>
                          {formatCurrency(preview.new_subscription.total_cost)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Billing Details */}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                {/* Effective Date */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-600">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    <span>Effective Date:</span>
                  </div>
                  <span className="font-medium">
                    {preview.immediate
                      ? "Immediately"
                      : formatDate(preview.effective_date)}
                  </span>
                </div>

                {/* Proration if applicable */}
                {preview.proration_estimate > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-gray-600">
                      <CurrencyPoundIcon className="h-4 w-4 mr-2" />
                      <span>Proration Charge:</span>
                    </div>
                    <span className="font-medium text-blue-600">
                      {formatCurrency(preview.proration_estimate)}
                    </span>
                  </div>
                )}

                {/* Next Charge */}
                {preview.next_charge.date && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Next billing date:</span>
                    <span className="font-medium">
                      {formatDate(preview.next_charge.date)}
                    </span>
                  </div>
                )}

                {/* Annual Savings */}
                {preview.savings > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3 mt-4">
                    <p className="text-sm text-green-800">
                      💰 You&apos;ll save {formatCurrency(preview.savings)} per
                      year with annual billing!
                    </p>
                  </div>
                )}
              </div>

              {/* Important Notes */}
              {!preview.immediate && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-amber-800">
                        Important
                      </h3>
                      <p className="mt-1 text-sm text-amber-700">
                        {preview.is_upgrade
                          ? "This change will take effect immediately and you'll be charged a prorated amount."
                          : "This change will take effect at the end of your current billing period. You'll continue to have access to your current plan until then."}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  disabled={parentLoading}
                  className={`px-4 py-2 rounded-md text-white ${
                    preview.is_upgrade
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-amber-600 hover:bg-amber-700"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {parentLoading
                    ? "Processing..."
                    : preview.immediate
                      ? "Confirm & Pay Now"
                      : "Schedule Change"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
